import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, CheckCircle2, Copy, Loader2, X, XCircle } from 'lucide-react';

import type { AssetQualityControlMetadata } from '../utils/assetQualityControl';
import {
  filterDisplayQualityControlIssues,
  formatQualityControlCopyText,
  hasGeometryFigureQaSignal,
  isQualityControlExecutionError,
  isQualityControlPendingExtraction,
  resolveQualityControlFailureSummary,
  sanitizeQualityControlForDisplay,
  shouldShowGeometryFigureQaSection,
} from '../utils/assetQualityControl';

type AssetQualityControlModalProps = {
  open: boolean;
  loading?: boolean;
  loadingMessage?: string;
  qualityControl: AssetQualityControlMetadata | null;
  runErrorMessage?: string | null;
  reextracting?: boolean;
  particleCount?: number;
  onClose: () => void;
  onReextract: () => void;
};

function StatusRow({ label, passed }: { label: string; passed?: boolean }) {
  const ok = passed === true;
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 size={18} className="shrink-0 text-emerald-600" />
      ) : (
        <XCircle size={18} className="shrink-0 text-danger" />
      )}
      <span className="text-sm font-medium text-ink">{label}</span>
    </div>
  );
}

function formatQuestionImageCorrelation(value: string | null | undefined): string {
  switch (value) {
    case 'strong':
      return '强相关';
    case 'moderate':
      return '中等相关';
    case 'weak':
      return '弱相关';
    case 'none':
      return '无相关';
    default:
      return '—';
  }
}

export function AssetQualityControlModal({
  open,
  loading = false,
  loadingMessage = '正在加载品控结果…',
  qualityControl,
  runErrorMessage = null,
  reextracting = false,
  particleCount,
  onClose,
  onReextract,
}: AssetQualityControlModalProps) {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayQualityControl = sanitizeQualityControlForDisplay(qualityControl);
  const imageContent = displayQualityControl?.image_content;
  const particleAlignment = displayQualityControl?.particle_alignment;
  const multimodalAnalysis = displayQualityControl?.multimodal_analysis;
  const geometryFigureQa = displayQualityControl?.geometry_figure_qa;
  const showGeometryFigureQa = shouldShowGeometryFigureQaSection(displayQualityControl);
  const pendingExtraction = isQualityControlPendingExtraction(particleCount);
  const hasQualityControlResult = Boolean(qualityControl?.last_checked_at);
  const showMultimodalSection = hasQualityControlResult;
  const canCopy = hasQualityControlResult;
  const reextractDisabled = reextracting || (loading && !pendingExtraction);
  const failureSummary = resolveQualityControlFailureSummary(qualityControl, runErrorMessage);
  const showFailureBanner = Boolean(
    failureSummary
    && hasQualityControlResult
    && (qualityControl?.overall_passed === false || isQualityControlExecutionError(qualityControl)),
  );
  const showFailureOnly = !loading && !hasQualityControlResult;
  const failureBannerTitle = isQualityControlExecutionError(qualityControl)
    ? '品控执行失败'
    : '品控未通过';
  const imageContentIssues = filterDisplayQualityControlIssues(imageContent?.issues);
  const particleAlignmentIssues = filterDisplayQualityControlIssues(particleAlignment?.issues);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => () => {
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleCopy = useCallback(() => {
    const copyText = formatQualityControlCopyText(qualityControl);
    if (!copyText) return;
    void navigator.clipboard.writeText(copyText)
      .then(() => {
        setCopied(true);
        if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = setTimeout(() => {
          setCopied(false);
          copiedTimerRef.current = null;
        }, 1800);
      })
      .catch(() => setCopied(false));
  }, [qualityControl]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="asset-qc-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 id="asset-qc-title" className="text-base font-semibold text-ink">品控</h2>
              {canCopy ? (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted hover:bg-canvas hover:text-ink"
                  title={copied ? '已复制' : '复制品控结果'}
                  aria-label={copied ? '已复制' : '复制品控结果'}
                >
                  {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                </button>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-muted">检查原图、内容提取与知识颗粒是否对齐</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted hover:bg-canvas hover:text-ink"
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {pendingExtraction ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-3">
              <p className="text-xs font-semibold text-danger">尚未提取知识颗粒</p>
              <p className="mt-1 text-sm text-red-900/80">请先提取知识颗粒后再查看品控结果。</p>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center gap-2 py-10 text-muted">
              <Loader2 size={22} className="animate-spin text-primary" />
              <p className="text-xs">{loadingMessage}</p>
            </div>
          ) : showFailureOnly ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-3">
              <p className="text-xs font-semibold text-danger">品控执行失败</p>
              <p className="mt-1 text-sm text-red-900/80">
                {failureSummary || '品控未能完成，请稍后重试'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {showFailureBanner ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-3">
                  <p className="text-xs font-semibold text-danger">{failureBannerTitle}</p>
                  <p className="mt-1 text-sm text-red-900/80">{failureSummary}</p>
                </div>
              ) : null}

              <div className="space-y-1.5">
                <StatusRow label="原图与内容提取" passed={imageContent?.passed} />
                {imageContent?.image_subject || imageContent?.extraction_category ? (
                  <p className="text-sm text-ink/85">
                    图片学科 {imageContent?.image_subject || '—'} · 提取学科 {imageContent?.extraction_category || '—'}
                  </p>
                ) : null}
                {imageContent?.subject_deviation_reason ? (
                  <p className="text-xs text-muted">{imageContent.subject_deviation_reason}</p>
                ) : null}
                {imageContent?.content_fidelity_summary ? (
                  <p className="text-xs text-muted">{imageContent.content_fidelity_summary}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <StatusRow label="内容提取与知识颗粒" passed={particleAlignment?.passed} />
                {particleAlignment?.summary ? (
                  <p className="text-xs text-muted">{particleAlignment.summary}</p>
                ) : null}
                {typeof particleAlignment?.particle_count === 'number' ? (
                  <p className="text-sm text-ink/85">
                    关联颗粒数：{particleAlignment.particle_count}
                    {particleAlignment.content_scope === 'single_question'
                      ? '（单题素材，1 个颗粒属正常）'
                      : ''}
                  </p>
                ) : null}
              </div>

              {particleAlignment?.answer_leakage_detected ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
                  <p className="text-xs font-semibold text-amber-900">题干疑似包含答案</p>
                  <p className="mt-1 text-sm text-amber-950/80">
                    {particleAlignment.answer_leakage_summary
                      || '请检查题干是否把最终答案、正确选项或结论写进了题目本身。'}
                  </p>
                </div>
              ) : null}

              {qualityControl?.subject_corrected ? (
                <div className="rounded-xl border border-border bg-canvas px-3 py-2 text-sm text-ink/85">
                  已自动纠正学科：{qualityControl.subject_corrected_from_extraction || '—'}
                  {' → '}
                  {qualityControl.subject_corrected_to || '—'}
                </div>
              ) : null}

              {showMultimodalSection ? (
                <div className={`rounded-xl border px-3 py-3 ${
                  multimodalAnalysis?.recommended
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-border bg-canvas'
                }`}
                >
                  <p className="text-xs font-semibold text-ink">多模态分析建议</p>
                  <p className="mt-1 text-sm text-ink/85">
                    图文相关度：{formatQuestionImageCorrelation(multimodalAnalysis?.question_image_correlation)}
                  </p>
                  <p className={`mt-1 text-sm font-medium ${
                    multimodalAnalysis?.recommended ? 'text-emerald-700' : 'text-muted'
                  }`}
                  >
                    {multimodalAnalysis?.recommended ? '建议使用多模态' : '可不强制多模态'}
                  </p>
                  {multimodalAnalysis?.reason ? (
                    <p className="mt-1 text-xs text-muted">{multimodalAnalysis.reason}</p>
                  ) : null}
                  {multimodalAnalysis?.visual_condition_gap_detected ? (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50/80 px-2.5 py-2">
                      <p className="text-xs font-semibold text-amber-900">视觉条件缺口</p>
                      <p className="mt-0.5 text-xs text-amber-950/80">
                        {multimodalAnalysis.visual_condition_gap_summary
                          || '题目条件可能依赖图中信息，请结合配图审阅。'}
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {showGeometryFigureQa ? (
                <div className="rounded-xl border border-border bg-canvas px-3 py-3">
                  <p className="text-xs font-semibold text-ink">几何图专项</p>
                  {hasGeometryFigureQaSignal(geometryFigureQa) ? (
                    <>
                      <p className="mt-1 text-sm text-ink/85">
                        检测到几何图：{geometryFigureQa?.detected ? '是' : '否'}
                        {' · '}
                        与题目相关：{geometryFigureQa?.question_related ? '是' : '否'}
                      </p>
                      <p className="mt-1 text-sm text-ink/85">
                        {geometryFigureQa?.score != null && Number.isFinite(Number(geometryFigureQa.score))
                          ? `几何图 QA 分：${Number(geometryFigureQa.score).toFixed(2)}`
                          : '几何图 QA 分：未评分（未满足评分条件）'}
                      </p>
                      {geometryFigureQa?.summary ? (
                        <p className="mt-1 text-xs text-muted">{geometryFigureQa.summary}</p>
                      ) : null}
                    </>
                  ) : (
                    <p className="mt-1 text-xs text-muted">
                      本次品控未包含几何判断，可重新运行品控获取。
                    </p>
                  )}
                </div>
              ) : null}

              {(imageContentIssues.length || particleAlignmentIssues.length) ? (
                <div className="rounded-xl border border-border bg-canvas px-3 py-3">
                  {imageContentIssues.map((issue, index) => (
                    <p key={`img-${index}`} className="text-sm text-ink/85">• {issue}</p>
                  ))}
                  {particleAlignmentIssues.map((issue, index) => (
                    <p key={`kp-${index}`} className="text-sm text-ink/85">• {issue}</p>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm text-muted hover:bg-canvas hover:text-ink"
          >
            关闭
          </button>
          <button
            type="button"
            disabled={reextractDisabled}
            onClick={onReextract}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {reextracting ? <Loader2 size={14} className="animate-spin" /> : null}
            {reextracting ? '重新提取中…' : '重新提取'}
          </button>
        </div>
      </div>
    </div>
  );
}
